// Issue #23 (landing): waar komt een aangemelde gebruiker terecht? Puur (testbaar) en gedeeld door de route-gate,
// de weigerpagina en het activatiescherm.
//
// Waarom niet gewoon "/": auth/me geeft ook pagina's voor een activeerbare rol die nog NIET loopt (die tonen dan
// vergrendelde knoppen). Wie enkel zo'n rol heeft (bv. alleen Beheer, niet geactiveerd), landde op het Overzicht, kreeg
// daar 403 op bedrijfsdata en belandde op "Geen toegang", waarvan de knop weer naar het Overzicht ging (lus).
import { NAV_ITEMS } from "config/navigation";
import PAGE_CATALOG from "config/generated/pageCatalog.json";
import { grantIsActive } from "utils/capabilities";

// Rollen zonder bedrijfsdata: met enkel deze rollen is het Overzicht (alles bedrijfsdata) geen zinvolle landing.
const NON_BUSINESS_ROLES = new Set(["BEHEER", "ASSISTENT"]);

// De pagina waar een rol het werk doet; gaat voor op de menuvolgorde zodra het Overzicht wegvalt (Beheer → Toegang).
const ROLE_LANDING = { BEHEER: "access" };

const upper = (v) => String(v || "").toUpperCase();

function rolesOf(access) {
  return Array.isArray(access?.roles) ? access.roles : [];
}

// De rolcodes die NU tellen (niet-activeerbaar, of activeerbaar met een lopende activatie).
export function activeRoleCodes(access, now = Date.now()) {
  return new Set(rolesOf(access).filter((r) => grantIsActive(r, now)).map((r) => upper(r.code)));
}

// Telt er NU minstens één rol? Server-vlag (auth/me access.hasActiveRole) of, bij een oudere API, uit de rollen zelf.
export function hasActiveRole(access, now = Date.now()) {
  if (!access || typeof access !== "object") return true; // single-modus: geen rolinfo, oud gedrag
  if (rolesOf(access).length > 0) return activeRoleCodes(access, now).size > 0;
  return typeof access.hasActiveRole === "boolean" ? access.hasActiveRole : false;
}

// De eigen activeerbare toewijzingen die nu NIET lopen: [{ code, name, idAssignment, maxHours }].
export function pendingActivations(access, now = Date.now()) {
  if (!access || typeof access !== "object") return [];
  const list = Array.isArray(access.activatable)
    ? access.activatable
    : rolesOf(access).filter((r) => r?.isActivatable && !grantIsActive(r, now));
  return list
    .filter((r) => r && r.idAssignment != null)
    .filter((r) => {
      // De klok van de browser telt mee: een rol die intussen wel loopt, hoort er niet meer bij.
      const grant = rolesOf(access).find((g) => g.idAssignment === r.idAssignment);
      return !grant || !grantIsActive(grant, now);
    })
    .map((r) => ({
      code: upper(r.code),
      name: r.name || r.code,
      idAssignment: r.idAssignment,
      maxHours: Number(r.maxHours) > 0 ? Number(r.maxHours) : 8,
    }));
}

// Aangemeld, met een rol, maar niets telt nu en er valt iets te activeren: het activatiescherm.
export function needsActivation(info = {}, now = Date.now()) {
  const { allowed, hasRequiredRole, waiting, access } = info;
  if (!allowed || !hasRequiredRole || waiting || !access) return false;
  return !hasActiveRole(access, now) && pendingActivations(access, now).length > 0;
}

// De page-keys waar de gebruiker NU zinvol kan landen, in menuvolgorde: zichtbaar volgens auth/me én via een rol die nu
// telt (pageCatalog), en het Overzicht enkel met een actieve bedrijfsrol. Zonder rolinfo: de pagina's van auth/me.
export function landingKeys(info = {}, now = Date.now()) {
  const pages = Array.isArray(info.pages) ? info.pages : [];
  const ordered = NAV_ITEMS.map((i) => i.pageKey).filter((k, i, all) => all.indexOf(k) === i && pages.includes(k));
  const roles = rolesOf(info.access);
  if (roles.length === 0) return ordered;
  const active = activeRoleCodes(info.access, now);
  const business = [...active].some((c) => !NON_BUSINESS_ROLES.has(c));
  return ordered.filter((key) => {
    if (key === "home" && !business) return false;
    const pageRoles = PAGE_CATALOG[key];
    if (!Array.isArray(pageRoles)) return true; // onbekend in de catalogus: auth/me beslist
    return pageRoles.some((r) => active.has(upper(r)));
  });
}

// De eerste pagina om op te landen ({ key, path, label }) of null. Het Overzicht als het kan; anders de werkpagina van een
// (pas geactiveerde) rol, anders de eerste in het menu. `prefer` = rolcodes die voorrang krijgen (net geactiveerd).
export function firstLanding(info = {}, { prefer = [], now = Date.now() } = {}) {
  const keys = landingKeys(info, now);
  if (keys.length === 0) return null;
  const preferred = [...prefer.map(upper), ...activeRoleCodes(info.access, now)]
    .map((c) => ROLE_LANDING[c])
    .find((k) => k && keys.includes(k));
  const key = prefer.length > 0 && preferred ? preferred : keys.includes("home") ? "home" : preferred || keys[0];
  const item = NAV_ITEMS.find((i) => i.pageKey === key);
  return item ? { key, path: item.path, label: item.label } : null;
}
